import React, { useEffect, useState, useRef, memo } from 'react';
import { useSelector } from 'react-redux';
import { replace } from 'ramda';
import classNames from 'classnames';
import { Box, CircularProgress, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AlyceTheme, Icon, SanitizedHTML } from '@alycecom/ui';

import {
  getMessage,
  getTimeout,
  getTitle,
  getUrl,
} from '../../modules/RecipientModule/store/redirect/redirect.selectors';
import StyledHeader from '../../modules/RecipientModule/components/StyledHeader/StyledHeader';
import useRedirect from '../../hooks/useRedirect';

import Footer from './Footer';

const useStyles = makeStyles<AlyceTheme>(({ palette, spacing }) => ({
  root: {
    position: 'fixed',
    width: '100%',
    minHeight: '100vh',
    zIndex: 1500,
    backgroundColor: palette.common.white,
    overflowY: 'auto',
  },
  icon: {
    marginRight: spacing(1),
  },
  title: {
    textAlign: 'center',
  },
  message: {
    textAlign: 'center',
    overflowWrap: 'break-word',
    marginBottom: spacing(8),
    '& > a': {
      wordBreak: 'break-word',
    },
  },
}));

const Redirect = (): JSX.Element | null => {
  const classes = useStyles();

  const redirectUrl = useSelector(getUrl);
  const timeout = useSelector(getTimeout);
  const message = useSelector(getMessage);
  const title = useSelector(getTitle);

  const [redirectState, setRedirectState] = useState({ countdown: 0, message });

  useRedirect(!!redirectUrl, redirectUrl, timeout);

  const localCountdownMessageTemplate = `${message} in\xA0a\xA0%COUNTDOWN%\xA0seconds...`;
  useEffect(() => {
    const localCountdown = Math.floor(timeout / 1000);
    if (localCountdown > 0) {
      setRedirectState({
        countdown: localCountdown,
        message: replace('%COUNTDOWN%', String(localCountdown), localCountdownMessageTemplate),
      });
    }
  }, [timeout, localCountdownMessageTemplate]);

  const countdownIntervalRef = useRef<number>();
  useEffect(() => {
    const { current: id } = countdownIntervalRef;
    if (redirectState.countdown > 0 && !id) {
      countdownIntervalRef.current = (setInterval(() => {
        setRedirectState(({ countdown }) => {
          const countdownValue = countdown > 1 ? countdown - 1 : countdown;
          return {
            countdown: countdownValue,
            message: replace('%COUNTDOWN%', String(countdownValue), localCountdownMessageTemplate),
          };
        });
      }, 1000) as unknown) as number;
    }
    return () => {
      if (redirectState.countdown === 1) {
        clearInterval(id);
      }
    };
  }, [redirectState, countdownIntervalRef, localCountdownMessageTemplate]);

  if (!redirectUrl) {
    return null;
  }

  return (
    <Box className={classes.root} bgcolor="common.white">
      <StyledHeader />
      <Box
        mt={[5, 11]}
        px={3}
        width={1}
        height={['calc(100vh - 56px)', 'calc(100vh - 80px)']}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        {!redirectState.countdown && <CircularProgress size={50} color="primary" />}
        {title && (
          <Box mt={3} display="flex" alignItems="center" justifyContent="center">
            <Icon className={classes.icon} icon="check" size="lg" color="default-salad" />
            <Typography data-testid="redirectPageTitle" className={classNames('H2-Dark', classes.title)}>
              {title}
            </Typography>
          </Box>
        )}
        {message && (
          <Box
            mt={4}
            width={[250, 'auto']}
            maxWidth={700}
            data-testid="redirectPageMessage"
            className={classNames('H4-Dark', classes.message)}
          >
            <SanitizedHTML html={(redirectState.message as string) || ''} />
          </Box>
        )}
        <Footer textColor="common.white" mobileHidden />
      </Box>
    </Box>
  );
};

export default memo(Redirect);
